import { create } from "zustand";

export type Direction = "up" | "down" | "flat";
export type BetSide = "up" | "down";
export type Outcome = "win" | "loss" | "draw";
export type ModalType = "menu" | "tasks" | "frens" | "leaderboard" | "onboarding" | "autobot" | null;

export type BetPhase =
  | { kind: "idle" }
  | { kind: "pending"; side: BetSide; amount: number; priceAtBet: number; secondsLeft: number }
  | { kind: "result"; outcome: Outcome; side: BetSide; amount: number; payoutDelta: number; tokensDelta: number };

export const COUNTDOWN_SECONDS = 5;
const STARTING_BALANCE = 10_000;
const DEFAULT_BET = 100;

const API_URL = import.meta.env.VITE_API_URL || "";
const getAuth = () => window.Telegram?.WebApp?.initData || "mock";
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `tma ${getAuth()}`,
});

export type LeaderboardEntry = {
  rank: number;
  userId: number;
  username: string;
  netProfit: number;
  dailyGt: number;
};

type GameState = {
  activeModal: ModalType;

  // Price feed
  price: number | null;
  prevPrice: number | null;
  direction: Direction;

  // Economy
  balanceUsd: number;
  tokens: number;

  // Respawn
  respawnAt: Date | null;      // when balance will be restored
  respawnMinutes: number;      // current respawn timer setting

  // Bet
  betAmount: number;
  phase: BetPhase;

  // Leaderboard
  leaderboard: LeaderboardEntry[];
  myRank: number | null;
  leaderboardNextPayout: Date | null;
  leaderboardLoading: boolean;

  // Streak
  streakBonus: number; // GT awarded on this session open

  // Actions
  openModal: (m: ModalType) => void;
  closeModal: () => void;
  setPrice: (price: number) => void;
  setBetAmount: (n: number) => void;
  initSession: () => Promise<void>;
  placeBet: (side: BetSide) => Promise<boolean>;
  tickCountdown: () => void;
  dismissResult: () => void;
  buyBoost: (type: "fast" | "turbo" | "instant") => Promise<boolean>;
  loadLeaderboard: () => Promise<void>;
  shareResult: (payoutDelta: number, side: BetSide) => void;

  // Auto-bot
  autoBotActive: boolean;
  autoBotHoursLeft: number;
  autoBotBetAmount: number;
  startAutoBot: (hours: number, betAmount: number) => Promise<boolean>;
  stopAutoBot: () => Promise<void>;
  fetchAutoBotStatus: () => Promise<void>;
};

export const useGame = create<GameState>((set, get) => ({
  activeModal: null,

  price: null,
  prevPrice: null,
  direction: "flat",

  balanceUsd: STARTING_BALANCE,
  tokens: 0,

  respawnAt: null,
  respawnMinutes: 60,

  betAmount: DEFAULT_BET,
  phase: { kind: "idle" },

  leaderboard: [],
  myRank: null,
  leaderboardNextPayout: null,
  leaderboardLoading: false,

  streakBonus: 0,

  autoBotActive: false,
  autoBotHoursLeft: 0,
  autoBotBetAmount: 100,

  openModal: (m) => set({ activeModal: m }),
  closeModal: () => set({ activeModal: null }),

  setPrice: (price) =>
    set((s) => {
      if (s.price === null) return { price, prevPrice: null, direction: "flat" };
      if (price === s.price) return { price, prevPrice: s.price };
      return { price, prevPrice: s.price, direction: price > s.price ? "up" : "down" };
    }),

  setBetAmount: (n) => {
    const bal = get().balanceUsd;
    set({ betAmount: Math.max(1, Math.min(Math.round(n), Math.floor(bal))) });
  },

  initSession: async () => {
    try {
      const res = await fetch(`${API_URL}/api/me`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json() as {
        balance: number;
        tokens: number;
        respawnAt: string | null;
        respawnMinutes: number;
        streakBonus: number;
        firstName?: string;
      };

      set({
        balanceUsd: data.balance,
        tokens: data.tokens,
        respawnAt: data.respawnAt ? new Date(data.respawnAt) : null,
        respawnMinutes: data.respawnMinutes,
        streakBonus: data.streakBonus ?? 0,
      });
    } catch (err) {
      console.error("initSession failed", err);
    }
  },

  placeBet: async (side) => {
    const { phase, price, balanceUsd, betAmount } = get();
    if (phase.kind !== "idle" || price === null) return false;
    if (betAmount <= 0 || betAmount > balanceUsd) return false;

    // Optimistic debit + start UI countdown
    set({
      balanceUsd: balanceUsd - betAmount,
      phase: { kind: "pending", side, amount: betAmount, priceAtBet: price, secondsLeft: COUNTDOWN_SECONDS },
    });

    try {
      const res = await fetch(`${API_URL}/api/bet`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ side: side.toUpperCase(), amount: betAmount }),
      });
      const data = await res.json() as {
        result: string; payout: number; tokensEarned: number;
        newBalance: number; newTokens: number; error?: string;
      };
      if (!res.ok) throw new Error(data.error);

      const currentPhase = get().phase;
      if (currentPhase.kind === "pending") {
        const newBalance = data.newBalance;
        set({
          balanceUsd: newBalance,
          tokens: data.newTokens,
          // Start respawn timer if we hit zero
          respawnAt: newBalance < 1 ? new Date(Date.now() + get().respawnMinutes * 60_000) : null,
          phase: {
            kind: "result",
            outcome: (data.result as string).toLowerCase() as Outcome,
            side: currentPhase.side,
            amount: betAmount,
            payoutDelta: data.payout - betAmount,
            tokensDelta: data.tokensEarned ?? 0,
          },
        });
      }
    } catch (err) {
      console.error("placeBet failed", err);
      set({ phase: { kind: "idle" }, balanceUsd });
    }

    return true;
  },

  tickCountdown: () => {
    const { phase } = get();
    if (phase.kind !== "pending") return;
    const next = phase.secondsLeft - 1;
    set({ phase: { ...phase, secondsLeft: Math.max(0, next) } });
  },

  dismissResult: () => {
    const { betAmount, balanceUsd } = get();
    set({
      phase: { kind: "idle" },
      betAmount: Math.max(1, Math.min(betAmount, Math.floor(balanceUsd))),
    });
  },

  buyBoost: async (type) => {
    try {
      const res = await fetch(`${API_URL}/api/boost`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ type }),
      });
      const data = await res.json() as {
        success: boolean; tokens: number; balance: number;
        respawnAt: string | null; error?: string;
      };
      if (!res.ok || !data.success) return false;
      set({
        tokens: data.tokens,
        balanceUsd: data.balance,
        respawnAt: data.respawnAt ? new Date(data.respawnAt) : null,
      });
      return true;
    } catch {
      return false;
    }
  },

  loadLeaderboard: async () => {
    set({ leaderboardLoading: true });
    try {
      const res = await fetch(`${API_URL}/api/leaderboard`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json() as {
        entries: LeaderboardEntry[];
        myRank: number | null;
        nextPayoutAt: string;
      };
      set({
        leaderboard: data.entries,
        myRank: data.myRank,
        leaderboardNextPayout: new Date(data.nextPayoutAt),
      });
    } finally {
      set({ leaderboardLoading: false });
    }
  },

  startAutoBot: async (hours, betAmount) => {
    try {
      const res = await fetch(`${API_URL}/api/autobot/start`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ hours, betAmount }),
      });
      const data = await res.json() as { success: boolean; hoursLeft: number; tokens: number; error?: string };
      if (!res.ok || !data.success) return false;
      set({ autoBotActive: true, autoBotHoursLeft: data.hoursLeft, autoBotBetAmount: betAmount, tokens: data.tokens });
      return true;
    } catch { return false; }
  },

  stopAutoBot: async () => {
    try {
      await fetch(`${API_URL}/api/autobot/stop`, { method: "POST", headers: authHeaders() });
      set({ autoBotActive: false, autoBotHoursLeft: 0 });
    } catch { /* ignore */ }
  },

  fetchAutoBotStatus: async () => {
    try {
      const res = await fetch(`${API_URL}/api/autobot/status`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json() as { active: boolean; hoursLeft: number; betAmount: number };
      set({ autoBotActive: data.active, autoBotHoursLeft: data.hoursLeft, autoBotBetAmount: data.betAmount });
    } catch { /* ignore */ }
  },

  shareResult: (payoutDelta, side) => {
    const tg = window.Telegram?.WebApp;
    const uid = tg?.initDataUnsafe?.user?.id;
    const refLink = uid
      ? `https://t.me/BurnTap_btc_bot?start=ref_${uid}`
      : "https://t.me/BurnTap_btc_bot";
    const won = payoutDelta > 0;
    const emoji = won ? "🔥" : "❄️";
    const sign = won ? "+" : "";
    const text = won
      ? `${emoji} Я передбачив BTC і виграв ${sign}$${Math.abs(payoutDelta).toFixed(0)} на BurnTap!\n\nСпробуй теж → ${refLink}`
      : `${emoji} Ставив на BTC ${side === "up" ? "вгору" : "вниз"}, але не вгадав. Спробую ще!\n\n${refLink}`;
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent(text)}`;
    tg?.openTelegramLink?.(shareUrl);
  },
}));
